import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import { Gift, Users, ShieldCheck, Heart } from 'lucide-react';

const Home = () => {
  const { user } = useAuth();

  useSEO({
    title: 'Coordinate Gifts & Share Wishlists Safely',
    description: 'Family Wishlist lets families create wishlists, coordinate holiday and birthday gifts without duplicate purchases, and keep surprises safe!',
    keywords: 'family wishlist, gift coordinator, holiday wishlist, gift list sharing, surprise safe gifts, secret wishlists',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': 'https://family-wishlist.com/#website',
          'url': 'https://family-wishlist.com/',
          'name': 'Family Wishlist',
          'description': 'Bringing families closer, one wishlist at a time.',
          'publisher': {
            '@type': 'Organization',
            'name': 'Family Wishlist',
            'url': 'https://family-wishlist.com/',
            'logo': 'https://family-wishlist.com/fw_logo.png'
          }
        },
        {
          '@type': 'WebApplication',
          'name': 'Family Wishlist',
          'applicationCategory': 'UtilitiesApplication',
          'operatingSystem': 'All',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          }
        }
      ]
    }
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <img
            src="/fw_logo.png"
            alt="Family Wishlist Logo"
            className="w-56 md:w-80 h-auto object-contain"
            onError={(e) => {
              // Fallback if image doesn't exist
              e.target.style.display = 'none';
            }}
          />
        </div>
        <h1 className="text-5xl font-extrabold text-xmas-red tracking-tight mb-4">
          Share Joy, Simple & Surprising
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
          Coordinate gifts for holidays, birthdays, and special family celebrations without ruining the surprise!
        </p>
        <div>
          {user ? (
            <Link to="/dashboard" className="btn-primary px-8 py-3 text-lg font-bold rounded-lg shadow-md hover:shadow-lg transition">
              Go to Dashboard
            </Link>
          ) : (
            <div className="flex justify-center gap-4">
              <Link to="/login" className="btn-primary px-8 py-3 text-lg font-bold rounded-lg shadow-md hover:shadow-lg transition">
                Sign In
              </Link>
              <Link to="/register" className="btn-secondary px-8 py-3 text-lg font-bold rounded-lg border border-gray-300 hover:shadow-md transition">
                Create Account
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
        <div className="card p-6 flex flex-col items-center text-center">
          <div className="p-3 bg-xmas-red/10 rounded-full mb-4">
            <Gift className="text-xmas-red w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Personal Wishlists</h3>
          <p className="text-gray-600 text-sm">
            Easily list gifts you’d love to receive. Add names, descriptions, and shop links to make it simple for others.
          </p>
        </div>

        <div className="card p-6 flex flex-col items-center text-center">
          <div className="p-3 bg-xmas-green/10 rounded-full mb-4">
            <Users className="text-xmas-green w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Family Sharing</h3>
          <p className="text-gray-600 text-sm">
            Create or join a private family group with a secure code. Share your list with all your groups simultaneously.
          </p>
        </div>

        <div className="card p-6 flex flex-col items-center text-center">
          <div className="p-3 bg-xmas-red/10 rounded-full mb-4">
            <Heart className="text-xmas-red w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Surprise-Safe</h3>
          <p className="text-gray-600 text-sm">
            Family members mark items as bought so there are no duplicate gifts. The recipient never knows what has been bought!
          </p>
        </div>

        <div className="card p-6 flex flex-col items-center text-center">
          <div className="p-3 bg-xmas-green/10 rounded-full mb-4">
            <ShieldCheck className="text-xmas-green w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Admin Curation</h3>
          <p className="text-gray-600 text-sm">
            Group administrators can manage family member lists and handle curation, keeping group spaces safe and friendly.
          </p>
        </div>
      </div>

      {/* Showcase / CTA Section */}
      <div className="bg-gray-50 rounded-2xl p-8 md:p-12 border border-gray-100 flex flex-col md:flex-row items-center gap-8">
        <div className="flex-1">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Gift Giving Made Stress-Free
          </h2>
          <p className="text-gray-600 mb-6">
            No more duplicate purchases, awkward text threads, or spoiled surprises. Family Wishlist is a secure, simple tool built to make coordinated gift giving a breeze. It is 100% free, run without ads, and respects your privacy.
          </p>
          {!user && (
            <Link to="/register" className="text-xmas-green hover:text-xmas-red font-bold inline-flex items-center gap-1 transition-colors">
              Get started in under 2 minutes &rarr;
            </Link>
          )}
        </div>
        <div className="flex-shrink-0 w-full md:w-80 h-48 bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col justify-center">
          <h4 className="font-bold text-xmas-green mb-2">💡 Quick Tip</h4>
          <p className="text-sm text-gray-600">
            Create "Child Lists" for your children under 13, and manage their wishlist details directly from your parent account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Home;
